"use client";

// ShareView — the shared-memory reading surface. One quiet column on the cream:
// the sharer's presence up top (their cell, their hue tinting the page), what
// was shared beneath it, and the sharer's netti resting at the right edge. No
// boxes anywhere — whitespace, hairlines, and type carry the structure
// (law_no_boxes).
//
// A SHARE OPENS EXACTLY WHAT WAS SHARED (law_share_scope), and what was shared
// is one of two shapes:
//
//   · a PAGE — the door opens onto that page's prose, or onto the skill
//     presentation when the page is a skill (law_skill_class).
//   · a FOLDER — the project root, or a folder inside it — and the door opens
//     onto a read-only SPACE (law_share_folder): the level exactly as the
//     dashboard shows it, sub-folders as the house tiles, pages as the house
//     list, everything navigable in place with the trail in `?at=`, and a page
//     opening in the same reader a solo share uses. Nothing here edits.
//
// The tree the space walks is the dashboard's own (`buildProjectSubtree`), built
// from the share payload — so which names are folders, where a skill gathers,
// what a page is called, all follow ONE set of rules (lib/os/tree).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import BrandCrumb from "../BrandCrumb";
import BrandIcon from "../BrandIcon";
import CellAvatar from "../CellAvatar";
import OrganismBackdrop from "../OrganismBackdrop";
import Markdown from "../Markdown";
import SkillMarkdown from "../skills/SkillMarkdown";
import DirIcon, { DirIconScaleStyle } from "../os/DirIcon";
import { PillButton } from "../ui";
import { useGlow } from "../useGlow";
import { organismFromSeed, seedFromString } from "../../lib/organism/identity";
import { useNaturalHeight } from "../../lib/os/useNaturalHeight";
import { useReadAge } from "../../lib/os/readAge";

import { relationOf } from "../../lib/stage/direction";
import Stage from "../stage/Stage";
import { useStageReadRamp } from "../stage/useStage";
import { spaceCss } from "../../lib/spaces/identity";
import { buildProjectSubtree, findByPath, seg, type OsNode } from "../../lib/os/tree";
import { parseSkillDoc, type ParsedSkill } from "../../lib/skills/manifest";
import type { SkillVersion } from "../../lib/skills/types";
import VersionsRail from "../skills/VersionsRail";
import { applyGroundVars, DEFAULT_BG, type RGB } from "../../lib/theme";
import "./share.css";
import "../stage/surfaces/share.css";
import { LinkBar } from "../skills/PasteUrl";

export type ShareState = "ok" | "signin" | "denied" | "not_found" | "legacy";

export interface SharePayload {
  token: string;
  path: string;
  visibility: string;
  /** what the sharer NAMED this door in the studio. Null = whatever the page is
   *  called — the title has always been the page's own, and still is by default. */
  title?: string | null;
  /** the ground the share carries (079). Painted by the page, not by this view. */
  ground?: string | null;
  space_name?: string | null;
  owner_key?: string | null;
  owner_name?: string | null;
  owner_avatar?: string | null;
  project: { id: string; slug: string; name: string; type: string; tagline: string | null; updated_at: string };
  dimensions: { name: string; content: string; lines: number }[];
  nodes: { node_id: string; type: string; content: Record<string, unknown> }[];
}

type ShareNode = SharePayload["nodes"][number];

function oneLiner(n: ShareNode): string {
  for (const f of ["role", "description", "status", "tagline", "summary", "context"]) {
    const v = n.content?.[f];
    if (typeof v === "string" && v.length > 0) return v.slice(0, 120);
  }
  return n.type;
}

/** what a cell is called — its own title when it carries one, else its id. */
function titleOf(n: ShareNode): string {
  for (const f of ["title", "name", "label"]) {
    const v = n.content?.[f];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return n.node_id;
}

/** a cell as a page: its fields as a quiet ledger in markdown, so the same
 *  reader that renders a dimension renders a cell. No heading: the page's name
 *  is already the headline over it. */
function bodyOf(n: ShareNode): string {
  const lines: string[] = [];
  for (const [k, v] of Object.entries(n.content ?? {})) {
    if (["title", "name", "label"].includes(k)) continue;
    const key = k.replace(/[_-]/g, " ");
    if (typeof v === "string") lines.push(`**${key}** ${v}`, "");
    else if (typeof v === "number" || typeof v === "boolean") lines.push(`**${key}** ${String(v)}`, "");
    else if (v != null) lines.push(`**${key}**`, "", "```json", JSON.stringify(v, null, 2), "```", "");
  }
  return lines.join("\n");
}

/** THE DASHBOARD'S OWN TREE, from the share payload — so a folder share shows
 *  exactly the level the sharer sees: the same folders, the same skills
 *  gathering, the same names and symbols (lib/os/tree is the one rule). */
function treeOf(share: SharePayload): OsNode {
  const when = share.project.updated_at;
  return buildProjectSubtree({
    id: share.project.id,
    slug: share.project.slug,
    name: share.project.name,
    type: share.project.type,
    summary: share.project.tagline ?? "",
    updatedAt: when,
    areas: share.dimensions.map((d) => ({ name: d.name, content: d.content, updatedAt: when })),
    memories: share.nodes.map((n) => ({
      id: n.node_id,
      type: n.type,
      title: titleOf(n),
      summary: oneLiner(n),
      body: bodyOf(n),
      updatedAt: when,
    })),
    artifacts: [],
    tasks: [],
    commits: [],
  });
}

/** how many pages a folder holds, all the way down. */
const countPages = (n: OsNode): number =>
  (n.children ?? []).reduce((a, c) => a + (c.icon === "page" ? 1 : countPages(c)), 0);

/** the `?at=` trail resolved to a node inside the shared folder: a path relative
 *  to the folder (`skills/brand-voice`), an absolute one, or a bare tail. */
function resolveAt(folder: OsNode, at: string | undefined): OsNode {
  if (!at) return folder;
  let want = at;
  try {
    want = decodeURIComponent(at);
  } catch {
    /* an odd escape reads as itself */
  }
  want = want.replace(/\/+$/, "");
  if (!want) return folder;
  const abs = want.startsWith("/") ? want : `${folder.path}/${want}`;
  const direct = findByPath(folder, abs);
  if (direct) return direct;
  const tail = want.split("/").filter(Boolean).pop()?.toLowerCase();
  if (!tail) return folder;
  const visit = (n: OsNode): OsNode | null => {
    for (const c of n.children ?? []) {
      if (c.path.split("/").pop() === tail || c.name.toLowerCase() === tail) return c;
      const deep = visit(c);
      if (deep) return deep;
    }
    return null;
  };
  return visit(folder) ?? folder;
}

function QuietState({ heading, body, signedIn }: { heading: string; body: string; signedIn: boolean }) {
  return (
    <main
      style={{
        minHeight: "100svh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.2rem",
        textAlign: "center",
        padding: "6rem 1.5rem 12vh",
        boxSizing: "border-box",
      }}
    >
      <h1 style={{ fontSize: "clamp(1.5rem, 3.4vw, 2rem)", fontWeight: 400, margin: 0, textWrap: "balance" }}>
        {heading}
      </h1>
      <p style={{ margin: 0, color: "var(--ink-2)", maxWidth: 440, textWrap: "pretty" }}>{body}</p>
      {!signedIn && (
        <a className="quiet-link" href="/login" style={{ fontSize: "1rem" }}>
          sign in
        </a>
      )}
    </main>
  );
}

/** what the reader is showing right now: a page's prose, and the skill it is
 *  when it is one. `door` = this is the very thing the link names (its title
 *  is the sharer's to write; its kept states are the rail's). */
interface Reading {
  name: string;
  content: string;
  skill: ParsedSkill | null;
  door: boolean;
}

export default function ShareView({
  state,
  share,
  at,
  signedIn,
  token,
  ownerGround,
  versions = [],
  viewing = null,
  preview = false,
}: {
  state: ShareState;
  share: SharePayload | null;
  /** the trail inside a shared folder (`?at=`), relative to the folder. */
  at?: string;
  signedIn: boolean;
  token: string;
  /** the kept states of the one page this door names, current first (084) */
  versions?: SkillVersion[];
  /** the superseded state being read, when `?v=` named one */
  viewing?: string | null;
  /** the SHARER's chosen ground colour — the page wears their room, not the
   *  default cream. Resolved server-side; null falls back to the house ground. */
  ownerGround?: RGB | null;
  /** RENDERED INSIDE THE SHARE STUDIO, not as the page itself.
   *
   *  The studio used to show this page in an iframe, which could not work and
   *  was slow even in theory: the share link redirects from the dashboard's host
   *  to the apex one, so the frame was cross-origin and `x-frame-options:
   *  SAMEORIGIN` refused it outright ("this content is blocked"); and a frame
   *  boots a second copy of the app, with a second organism, before the sharer
   *  can see anything — and only once a link has been minted at all.
   *
   *  So the studio renders THIS COMPONENT instead. Same component, so the
   *  preview cannot drift from the page. In preview it owns nothing global: no
   *  ground written to :root, no reading ramp, no organism calm, no scroll
   *  listener, and its creature is drawn inside the preview's own box rather
   *  than fixed to the viewport. Everything it needs comes down as props. */
  preview?: boolean;
}) {
  // the sharer's living color, scoped to this page.
  const ownerKey = share?.owner_key || `share:${token}`;
  const color = useMemo(() => organismFromSeed(ownerKey), [ownerKey]);
  const seed = useMemo(() => seedFromString(ownerKey), [ownerKey]);
  const css = useMemo(() => spaceCss(color), [color]);
  const glow = useGlow();

  // the netti on the right is the scroll bar — young at the head, grown by the
  // last line. Only once the page actually shows the sharer's room.
  useReadAge(state === "ok" && !preview);

  // paint the sharer's ground (and flip the ink regime with it) before first
  // paint — layout effect so it lands ahead of ThemeProvider's mount effect,
  // which skips /share and never clobbers it back to cream. Restored on leave.
  useLayoutEffect(() => {
    // in preview the ground is set ON THE PREVIEW BOX by the studio; writing it
    // to :root here would repaint the studio around it.
    if (!ownerGround || state !== "ok" || preview) return;
    applyGroundVars(ownerGround);
    return () => applyGroundVars(DEFAULT_BG.rgb);
  }, [ownerGround, state, preview]);

  // the reading ramp (law_read_ramp). A shared page plants the sharer's netti
  // full-bleed behind the prose, which is right at the top of the page and wrong
  // the moment someone actually reads: scrolling frosts it away so the words sit
  // in full contrast, and it clears again at the top. Opacity AND blur radius
  // both ride --art-ramp, and the same value rests the organism through the calm
  // bus, so browsing pays no gaussian (law_dynamic_frost). It hears the stage's
  // scroller (components/stage/useStage), and a preview is not being read: it
  // must not drive the page's reading ramp or rest the organism.
  useStageReadRamp(240, "art", { calmKey: "share-reader", active: state === "ok" && !preview });

  // ── what the door names ──────────────────────────────────────────────────
  // The dashboard's tree, built from the payload; the minted path walked into
  // it. A folder (the project root, `…/skills`, a group of cells) opens as the
  // SPACE; a page opens as the reader. A path the tree doesn't know (an older
  // link naming a page by its raw name) still finds the page by name.
  const tree = useMemo(() => (share ? treeOf(share) : null), [share]);
  const named = useMemo(() => {
    if (!tree || !share) return null;
    const parts = share.path.split("/").filter(Boolean);
    return findByPath(tree, `/${parts.join("/")}`);
  }, [tree, share]);
  const folder = named?.icon === "folder" ? named : null;

  const soloDim = useMemo(() => {
    if (named || !share) return null;
    const parts = share.path.split("/").filter(Boolean);
    // "projects/<id>" is the whole map (a folder above); deeper names a page.
    if (parts.length < 3) return null;
    const want = parts[parts.length - 1].toLowerCase();
    return share.dimensions.find((d) => seg(d.name) === want || d.name.toLowerCase() === want) ?? null;
  }, [named, share]);

  // ── the trail inside a shared folder ─────────────────────────────────────
  // Client state, mirrored into `?at=` (relative to the folder) with
  // replaceState — a deep link lands where it points, and the studio's preview
  // can follow the reader in. Nothing here is a navigation of the page itself.
  const [current, setCurrent] = useState<OsNode | null>(() => (folder ? resolveAt(folder, at) : null));
  // the trail re-resolves only when the DOOR changes (a new payload) — reset
  // during render, so the new folder never paints with the old level.
  const [seenFolder, setSeenFolder] = useState(folder);
  if (seenFolder !== folder) {
    setSeenFolder(folder);
    setCurrent(folder ? resolveAt(folder, at) : null);
  }

  // the link exactly as it stands — what the paste line offers, and what a
  // skill's "copy link" copies. Re-read after every step inside a folder.
  const [pageUrl, setPageUrl] = useState("");
  useEffect(() => {
    if (typeof location !== "undefined") setPageUrl(location.href);
  }, []);

  // every step inside a shared folder is a PLACE (law_every_place_an_address):
  // it has its own `?at=`, Back climbs it, and the content travels the way the
  // folder did (deeper is forward, out is back). In the studio's preview nothing
  // is pressed, and nothing pushes onto the studio's own history.
  const go = useCallback(
    (n: OsNode) => {
      if (!folder) return;
      setCurrent(n);
      try {
        const u = new URL(location.href);
        const rel = n.path === folder.path ? null : n.path.slice(folder.path.length + 1);
        if (rel) u.searchParams.set("at", rel);
        else u.searchParams.delete("at");
        if (u.toString() !== location.href) {
          if (preview) history.replaceState(null, "", u.toString());
          else history.pushState(null, "", u.toString());
        }
        setPageUrl(u.toString());
      } catch {
        /* the space still moves; only the trail in the url is lost */
      }
      // the stage opens each new place at its own top (no window scroll here)
    },
    [folder, preview],
  );
  useEffect(() => {
    if (!folder || preview) return;
    const onPop = () => {
      try {
        const u = new URL(location.href);
        setCurrent(resolveAt(folder, u.searchParams.get("at") ?? undefined));
        setPageUrl(u.toString());
      } catch {
        setCurrent(folder);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [folder, preview]);

  // the crumb: the shared folder, then every folder down to (not including)
  // where we stand — the current level is named beside them, not as a step.
  const trail = useMemo(() => {
    if (!folder || !current || current === folder) return [];
    const nodes: OsNode[] = [folder];
    const rest = current.path.slice(folder.path.length + 1).split("/").filter(Boolean);
    let p = folder.path;
    for (const s of rest) {
      p = `${p}/${s}`;
      if (p === current.path) break;
      const n = findByPath(folder, p);
      if (n) nodes.push(n);
    }
    return nodes;
  }, [folder, current]);

  // ── what is being read, if a page is ─────────────────────────────────────
  const reading = useMemo<Reading | null>(() => {
    if (folder) {
      if (!current || current.icon !== "page") return null;
      const content = current.document ?? "";
      return { name: current.name, content, skill: parseSkillDoc(content, seg(current.name)), door: false };
    }
    if (named?.icon === "page") {
      const content = named.document ?? "";
      return { name: named.name, content, skill: parseSkillDoc(content, seg(named.name)), door: true };
    }
    if (soloDim) {
      return {
        name: soloDim.name.replace(/[_-]/g, " "),
        content: soloDim.content,
        skill: parseSkillDoc(soloDim.content, seg(soloDim.name)),
        door: true,
      };
    }
    return null;
  }, [folder, current, named, soloDim]);
  const skill = reading?.skill ?? null;
  // the headline is the DOOR's (the sharer's title wins) at the thing the link
  // names; deeper inside a folder it is whatever is being read.
  const atDoor = folder ? current === folder : true;

  const [copied, setCopied] = useState(false);

  // law_scroll_lock — the head settles as the page moves under it: the sharer's
  // line folds away and the title settles into a bar. The stage folds it (it is
  // the stage's own head); the folding pieces publish their natural heights.
  const byRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  useNaturalHeight(byRef, "--lock-h");
  useNaturalHeight(titleRef, "--lock-h");
  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(pageUrl || location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* the link is on screen and selectable */
    }
  }, [pageUrl]);

  const updated = share
    ? new Date(share.project.updated_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

  // the space's children: sub-folders first, then pages (the dashboard's order)
  const level = folder && current && current.icon === "folder" ? current : null;
  const subFolders = level ? (level.children ?? []).filter((c) => c.icon === "folder") : [];
  const pages = level ? (level.children ?? []).filter((c) => c.icon === "page") : [];

  const headline = share
    ? atDoor
      ? share.title?.trim() ||
        (skill ? skill.manifest.title : reading ? reading.name : folder ? folder.name : share.project.name)
      : skill
        ? skill.manifest.title
        : (current?.name ?? share.project.name)
    : "";

  // THE STAGE — what the scene is (a place inside the shared folder, or the
  // door's own page) and which way the folder moved to reach it (deeper is
  // forward, out is back), read once at the render the move happens in.
  const sceneKey = folder && current ? current.path : "door";
  const travel = useRef<{ key: string; dir: 1 | -1 }>({ key: sceneKey, dir: 1 });
  if (travel.current.key !== sceneKey) {
    travel.current = { key: sceneKey, dir: relationOf(travel.current.key, sceneKey).dir };
  }

  return (
    <div
      className={`share-root${preview ? " share-root--preview" : ""}`}
      style={{
        ["--organism-hue" as string]: Math.round(color.primary.hue),
        ["--organism-color" as string]: css.core,
      }}
    >
      {/* the sharer's netti — seated at the RIGHT edge (the reader law,
          2026-08-10), young at the head of the page and fully grown by its last
          line: the creature is the scroll bar. Still the sharer's own colour and
          seed, so the room is unmistakably theirs. */}
      {state === "ok" && (
        <OrganismBackdrop
        readingFrost
          // the reader law: the sharer's words own the left, their netti the
          // right — and its age is the scroll bar (useStageReadAge).
          anchor="right"
          offset={{ x: "-6vw" }}
          size="min(130vh, 980px)"
          opacity={0.72}
          fade={0.82}
          seed={seed}
          color={color}
          age={0.38}
          spin
          grow
          warm
          macros={{ bleed: 0.5, shimmer: 0.4, glow: 0, grain: 0, density: 0.52, network: 0.5 }}
          live={false}
          publishHue={false}
          // in preview the creature belongs to the preview's own box: `fixed`
          // would plant it on the studio's viewport, outside the plate.
          fixed={!preview}
          ageBus={!preview}
          zIndex={0}
        />
      )}

      {/* the reading ground — frosts the sharer's netti away as you read, and
          clears again at the top. Above the organism (z0), under the words (z2). */}
      {state === "ok" && <div className="share-read-scrim" aria-hidden />}

      <header className="share-head">
        <div className="share-head-row">
          <BrandCrumb rootHref="/" segments={[{ label: "shared" }]} />
          <a className="quiet-link" href={signedIn ? "/dashboard" : "/signup"} style={{ fontSize: "0.9rem" }}>
            {signedIn ? "open yours" : "begin yours"}
          </a>
        </div>
      </header>

      {state === "signin" && (
        <QuietState
          heading="this memory is shared with a closed circle."
          body="sign in and, if the door is open for you, it will simply be here."
          signedIn={signedIn}
        />
      )}
      {state === "denied" && (
        <QuietState
          heading="this door doesn't open for this account."
          body="ask the person who shared it to widen the link, or to stand you in their space."
          signedIn={signedIn}
        />
      )}
      {state === "not_found" && (
        <QuietState
          heading="nothing lives at this link."
          body="it may have been let go, or never was. ask for a fresh one."
          signedIn={signedIn}
        />
      )}
      {state === "legacy" && (
        <QuietState
          heading="this link is from an older innernet."
          body="shares are quieter and safer now. ask the sharer to copy a fresh link from their folder."
          signedIn={signedIn}
        />
      )}

      {state === "ok" && share && (
        <Stage
          frame="none"
          fold="split"
          autoFold
          readFocus="always"
          // the page's own head, where it sat under the crumb bar; the title
          // settles into the bar it used to pin as (law_stage, title-settling)
          top="7.5rem"
          pin="clamp(3.6rem, 8vh, 4.6rem)"
          gap="1.1rem"
          sceneKey={sceneKey}
          dir={travel.current.dir}
          label={headline || "shared page"}
          // the studio's preview is a box inside a frame: it never takes the page
          scope={preview ? "contained" : "document"}
          rootProps={{ className: "stage--share" }}
          head={
            <div className="stage-share__head">
              {/* law_scroll_lock — the head holds while the shared page moves under
                  it, and collapses to the title: whose page this is folds away, the
                  title settles into a bar. ONE head for every shape (a skill, a
                  page, a folder) — they differ only in what the title says. */}
              <header className="share-head-lock">
                {/* the sharer's presence */}
                <div ref={byRef} className="os-lock__fold">
                  <div className="share-by os-enter">
                    {/* the one face — CellAvatar, same as every byline in the
                        product. `.share-root` has already handed it the sharer's
                        own organism colour, so it stays unmistakably theirs. */}
                    <CellAvatar
                      className="share-by-cell"
                      name={share.owner_name}
                      src={share.owner_avatar}
                      size={34}
                      fontSize="0.86rem"
                    />
                    <span className="share-by-line">
                      {skill ? "a skill, shared by " : folder && !reading ? "a folder, shared by " : "shared by "}
                      {share.owner_name ?? "someone"}
                      {share.space_name ? ` · from ${share.space_name}` : ""}
                    </span>
                  </div>
                </div>
                <div ref={titleRef} className="os-lock__title">
                  {/* `data-door` marks the headline as the DOOR's own — the one the
                      sharer names in the studio, and the one the studio's preview
                      writes the draft title into. Deeper inside a folder the
                      headline belongs to the page being read. */}
                  <h1 className="share-title os-enter" style={{ ["--i" as string]: 1 }} data-door={atDoor || undefined}>
                    {headline}
                  </h1>
                </div>
              </header>
            </div>
          }
        >
          <main className="share-body stage-share__body">

            {/* the trail inside a shared folder — quiet words, each a step back */}
            {trail.length > 0 && (
              <ol className="share-crumb os-enter" style={{ ["--i" as string]: 2 }} aria-label="where you are in this folder">
                {trail.map((n) => (
                  <li key={n.path}>
                    <button type="button" onClick={() => go(n)}>{n.name}</button>
                  </li>
                ))}
                {current && (
                  <li>
                    <span aria-current="page">{current.name}</span>
                  </li>
                )}
              </ol>
            )}

            {reading && skill ? (
              // ── a SKILL — the page IS the skill, wearing its sharer ──
              <>
                {/* THE PASTE LINE — the one sentence that explains the format. */}
                <div className="sk-paste os-enter" style={{ ["--i" as string]: 2 }}>
                  <p className="sk-paste-say">paste this link into your ai. it will know what to do.</p>
                  <LinkBar url={pageUrl || "…"} done={copied} onCopy={() => void copyLink()} />
                </div>

                <p className="sk-dek os-enter" style={{ ["--i" as string]: 3 }}>{skill.manifest.description}</p>

                <div className="detail-rows sk-ledger os-enter" style={{ ["--i" as string]: 4 }}>
                  <div className="detail-row"><span>kind</span><span>skill</span></div>
                  {skill.manifest.when_to_load && (
                    <div className="detail-row"><span>when to load it</span><span>{skill.manifest.when_to_load}</span></div>
                  )}
                  {skill.manifest.version && <div className="detail-row"><span>version</span><span>{skill.manifest.version}</span></div>}
                  <div className="detail-row"><span>kept by</span><span>{share.owner_name ?? "someone"}</span></div>
                  <div className="detail-row"><span>reading</span><span>{skill.minutes} min</span></div>
                </div>

                {/* every state this skill has been — read live from the keeper's
                    memory. The rail knows the ONE page the door names. */}
                {reading.door && <VersionsRail versions={versions} viewing={viewing} basePath={`/share/${token}`} />}

                <div className="share-rule" aria-hidden />
                <SkillMarkdown body={skill.body} />
              </>
            ) : reading ? (
              // ── one PAGE — the door's own, or one opened inside a shared folder ──
              <>
                <p className="share-meta os-enter" style={{ ["--i" as string]: 2 }}>
                  a page from {share.project.name} · tended {updated}
                </p>
                {reading.door && <VersionsRail versions={versions} viewing={viewing} basePath={`/share/${token}`} />}
                <div className="share-rule" aria-hidden />
                <section className="share-dim" data-focus>
                  <Markdown source={reading.content} />
                </section>
              </>
            ) : level ? (
              // ── a FOLDER — the level as the dashboard shows it, read-only ──
              <div className="share-space">
                <p className="share-meta os-enter" style={{ ["--i" as string]: 2 }}>
                  {subFolders.length > 0 && `${subFolders.length} ${subFolders.length === 1 ? "folder" : "folders"} · `}
                  {pages.length} {pages.length === 1 ? "page" : "pages"} · tended {updated}
                </p>
                <div className="share-rule" aria-hidden />
                <DirIconScaleStyle />

                {subFolders.length > 0 && (
                  <div className="share-tiles">
                    {subFolders.map((c, i) => {
                      const n = countPages(c);
                      return (
                        <button
                          key={c.key}
                          type="button"
                          className="share-tile os-enter"
                          style={{ ["--i" as string]: i } as React.CSSProperties}
                          onClick={() => go(c)}
                          aria-label={`open ${c.name}`}
                        >
                          <DirIcon icon="folder" symbol={c.symbol} size={84} color={c.color} />
                          <span className="share-tile-name">{c.name}</span>
                          <span className="share-tile-count">
                            {n} {n === 1 ? "page" : "pages"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {pages.length > 0 && (
                  <div className="list share-rows">
                    {pages.map((c, i) => (
                      <div
                        key={c.key}
                        role="button"
                        tabIndex={0}
                        className="os-enter list-row list-row--rich"
                        style={{ ["--i" as string]: subFolders.length + i } as React.CSSProperties}
                        onClick={() => go(c)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            go(c);
                          }
                        }}
                        onPointerMove={glow}
                      >
                        <DirIcon icon="page" symbol={c.symbol} size={56} color={c.color} />
                        <div className="list-rich__main">
                          <div className="list-rich__head">
                            <span className="list-name">{c.name}</span>
                          </div>
                          {c.summary ? <p className="list-sum">{c.summary}</p> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {subFolders.length === 0 && pages.length === 0 && (
                  <p className="share-empty os-enter" style={{ ["--i" as string]: 3 }}>nothing in here yet.</p>
                )}
              </div>
            ) : (
              // a path the tree can't place and no page by that name: the map's
              // name stands, and the door still says who kept it.
              <p className="share-meta os-enter" style={{ ["--i" as string]: 2 }}>
                nothing is at this path any more. ask the sharer for a fresh link.
              </p>
            )}

            {/* the gateway — the visitor arrived through someone they trust, so
                the page ends on one quiet door: no box, no banner — a closing
                line and THE netti pill, exactly the front door's own grammar
                (law_gateway). */}
            <section className="gateway" aria-labelledby="share-gate-h">
              <hr className="share-rule gateway-rule" aria-hidden />
              <h2 className="gateway-h" id="share-gate-h">
                {skill ? "keep it where your ai can reach it." : "this is a living memory. you can keep one too."}
              </h2>
              <p className="gateway-p">
                innernet is memory your ai tools read live: projects, decisions, skills, the
                things you'd otherwise re-explain every session.
                {skill ? " save this skill to yours, or publish your own as a link like this." : " save this to yours, or start your own and share it the same way."}
              </p>
              <div className="gateway-row">
                <PillButton
                  as="a"
                  href={
                    signedIn
                      ? `/dashboard?add=share:${token}${viewing ? `&v=${viewing}` : ""}`
                      : `/signup?from=share:${token}${viewing ? `&v=${viewing}` : ""}`
                  }
                  icon={<BrandIcon name="innernet" size={17} className="pill-netti" />}
                >
                  {viewing ? "save this version to your innernet" : "save it to your innernet"}
                </PillButton>
                <a className="quiet-link" href="/community">
                  browse the community&rsquo;s skills
                </a>
              </div>
            </section>
          </main>
        </Stage>
      )}
    </div>
  );
}
